@if (isLoaded) {
    <mat-dialog-content>
        <div class="flex-row flex-space-between p-20 bg-accent">
            <div>
                <mat-card-header>
                    <mat-card-title>
                        {{ this.editPost ? 'Edit' : 'New' }} discussion
                    </mat-card-title>
                </mat-card-header>
            </div>
            <div class="float-end">
                <button class="mat-dialog-close" mat-icon-button matTooltip="Close modal"
                        aria-label="Close discussion editor" (click)="exitCheck()">
                    <mat-icon class="accent-text">close</mat-icon>
                </button>
            </div>
        </div>
        <div class="p-20 pb-10">
            <form [formGroup]="createPostForm">
                <mat-form-field appearance="outline" class="w-100" color="accent">
                    <mat-label>Discussion Title</mat-label>
                    <input formControlName="title" matInput>
                    @if (createPostForm.controls['title'].hasError('required')) {
                        <mat-error>
                            This field is required
                        </mat-error>
                    }
                </mat-form-field>
                @if (communities.length > 1) {
                    <mat-form-field appearance="outline" class="w-100" color="accent">
                        <mat-label>Community</mat-label>
                        <mat-select formControlName="community" [disabled]="!!editPost && !canModerateEditedPost">
                            @for (community of communities; track community) {
                                <mat-option [value]="community.id">{{ community.name }}
                                </mat-option>
                            }
                        </mat-select>
                        @if (createPostForm.controls['community'].hasError('required')) {
                            <mat-error>
                                This field is required
                            </mat-error>
                        }
                    </mat-form-field>
                }
                @if (communityId && canModerateEditedPost && !(this.community?.members_can_only_post_in_member_subgroup && this.hasCommunityRole(['member']))) {
                    <mat-form-field appearance="outline" class="w-100 mb-10" color="accent">
                        <mat-label>Subgroups (optional)</mat-label>
                        <input [formControl]="subgroupSearchControl"
                               [matAutocomplete]="auto"
                               [matChipInputFor]="chipList"
                               matInput
                               placeholder="Select Subgroup">
                        <mat-autocomplete #auto="matAutocomplete"
                                          (optionSelected)="selectSubgroup($event.option.value)"
                                          [displayWith]="displayFn">
                            @for (subgroup of filteredSubgroups | async; track subgroup) {
                                @if (!checkIfInArray(subgroup, this.currentSubgroups)) {
                                    <mat-option
                                            [value]="subgroup">
                                        {{ subgroup.name }}
                                    </mat-option>
                                }
                            }
                        </mat-autocomplete>
                        <mat-chip-grid #chipList aria-label="Users Selection">
                            @for (subgroup of this.currentSubgroups; track subgroup) {
                                <mat-chip (removed)="removeSubgroup(subgroup)"
                                          class="smChip">
                                    {{ subgroup.name }}
                                    <button matChipRemove>
                                        <mat-icon>cancel</mat-icon>
                                    </button>
                                </mat-chip>
                            }
                        </mat-chip-grid>
                        @if (!subgroups.length) {
                            <mat-hint>You have no subgroups in this community</mat-hint>
                        } @else {
                            <mat-hint>Choose a subgroup to limit who sees this discussion, or leave this empty for the community.</mat-hint>
                        }
                    </mat-form-field>
                }
                @if (isSuperuser || hasCommunityRole(['full_admin', 'client'])) {
                    <mat-form-field
                            [class.pb-10]="!categories.length" appearance="outline" class="w-100"
                            color="accent">
                        <mat-label>Categories</mat-label>
                        <mat-select [disabled]="!categories.length" formControlName="categories" multiple
                                    placeholder="All">
                            @for (category of categories; track category) {
                                <mat-option
                                        [value]="category.id">{{ this.community?.member_generated_name && category.name == 'Member Generated' ? this.community?.member_generated_name : category?.name }}
                                </mat-option>
                            }
                        </mat-select>
                        @if (!categories.length) {
                            <mat-hint>You have no categories in your current community</mat-hint>
                        }
                    </mat-form-field>
                }
                <div class="flex-auto max-w-100">
                    @if (useBasicEditor) {
                        <p role="status">The formatting tools couldn’t load. You can still write and post your discussion below.</p>
                        <div #basicEditor class="basic-discussion-editor" contenteditable="true"
                             role="textbox" aria-label="Discussion text" aria-multiline="true"
                             [innerHTML]="basicEditorHtml" (input)="updateBasicEditor(basicEditor)"
                             (paste)="pasteBasicEditor($event, basicEditor)"
                             (blur)="textFormControl.markAsTouched()"></div>
                    } @else if (!editorReady) {
                        <p role="status">Loading discussion editor…</p>
                    }
                    <ckeditor (ready)="onReady($event)" (error)="onEditorError($event)"
                              [config]="config" [editor]="Editor" class="w-100" [class.d-none]="useBasicEditor"
                              [disableTwoWayDataBinding]="useBasicEditor"
                              formControlName="text" #ckHost></ckeditor>
                    @if (this.createPostForm.value.text.length <= 0) {
                        <small class="errorMsg">This field is required</small>
                    }
                </div>
                @if (canRenamePostButtons || canRenamePostLinkButton) {
                    <h3>Button text for this post</h3>
                    <div class="post-button-labels">
                        @if (canRenamePostButtons) {
                            <mat-form-field appearance="outline" class="w-100">
                                <mat-label>Vote button text</mat-label>
                                <input formControlName="vote_button_text" matInput maxlength="255"
                                       [placeholder]="community?.vote_button_text || 'Vote'">
                                <mat-hint>Leave blank to use the Community default.</mat-hint>
                                @if (createPostForm.controls['vote_button_text'].hasError('maxlength')) {
                                    <mat-error>Use 255 characters or fewer.</mat-error>
                                }
                            </mat-form-field>
                            <mat-form-field appearance="outline" class="w-100">
                                <mat-label>Attachment button text</mat-label>
                                <input formControlName="attachment_button_text" matInput maxlength="255"
                                       [placeholder]="community?.attachment_button_text || 'Attachment'">
                                <mat-hint>Leave blank to use the Community default.</mat-hint>
                                @if (createPostForm.controls['attachment_button_text'].hasError('maxlength')) {
                                    <mat-error>Use 255 characters or fewer.</mat-error>
                                }
                            </mat-form-field>
                        }
                        @if (canRenamePostLinkButton) {
                            <mat-form-field appearance="outline" class="w-100">
                                <mat-label>Link button text</mat-label>
                                <input formControlName="link_button_text" matInput maxlength="255" placeholder="View">
                                <mat-hint>Leave blank to use View.</mat-hint>
                                @if (createPostForm.controls['link_button_text'].hasError('maxlength')) {
                                    <mat-error>Use 255 characters or fewer.</mat-error>
                                }
                            </mat-form-field>
                        }
                    </div>
                }
                <div class="d-flex flex-row gap-10 mt-10 flex-wrap">
                    @if (hasCommunityRole(['member'])) {
                        <mat-checkbox formControlName="is_anonymous">Post Anonymously
                        </mat-checkbox>
                    }
                    @if (isSuperuser || hasCommunityRole(['full_admin', 'client'])) {
                        <mat-checkbox formControlName="is_closed_loop">
                            Mark Post
                            as {{ community?.close_the_loop_name ? community?.close_the_loop_name : 'Close the Loop' }}
                        </mat-checkbox>
                    }
                    @if (isSuperuser || hasCommunityRole(['full_admin'])) {
                        <mat-checkbox formControlName="is_pinned">Pin the post to the top of the feed</mat-checkbox>
                        <mat-checkbox formControlName="is_pinned_to_sidebar">Pin post in sidebar</mat-checkbox>
                        @if (!community?.hide_moderated) {
                            <mat-checkbox formControlName="hide_unmoderated_comments">
                                Hide un-moderated comments
                            </mat-checkbox>
                        }
                    }
                </div>
                @if (isClosedLoop) {
                    <mat-form-field [hidden]="!isClosedLoop" appearance="outline" class="w-100">
                        <mat-label>Close the loop parent</mat-label>
                        <input [formControl]="postSearchControl"
                               [matAutocomplete]="postAuto"
                               formControlName="original_post"
                               matInput
                               placeholder="Choose Post"
                               type="text">
                        @if (postSearchLoading) {
                            <mat-icon matSuffix>
                                <mat-spinner color="accent"></mat-spinner>
                            </mat-icon>
                        }
                        <mat-autocomplete #postAuto="matAutocomplete" [displayWith]="postDisplayFn">
                            @for (option of filteredPosts; track option) {
                                <mat-option [value]="option">{{ option.title }}
                                </mat-option>
                            }
                        </mat-autocomplete>
                    </mat-form-field>
                }
            </form>
            @if (canModerateEditedPost && (isSuperuser || hasCommunityRole(['full_admin', 'client']))) {
                <form [formGroup]="createPostForm">
                    <div class="mt-10 mb-10 d-flex align-items-center">
                        <mat-checkbox [formControl]="lockPostControl">lock a post with a pre-set time</mat-checkbox>
                    </div>
                    @if (isSuperuser || hasCommunityRole(['full_admin'])) {
                        <div class="mt-10 mb-10 d-flex align-items-center">
                            <mat-checkbox formControlName="is_hidden">Hide/schedule post</mat-checkbox>
                        </div>
                    }
                    <div [class.d-none]="!this.createPostForm.value.is_hidden" class="d-flex flex-row gap-10 mt-20">
                        <mat-form-field appearance="outline" color="accent">
                            <mat-label>Schedule post</mat-label>
                            <input [formControl]="revealDateControl" [matDatepicker]="revealPicker" matInput>
                            @if (revealDateControl.value) {
                                <mat-datepicker-toggle (click)="clearRevealDate()" matIconSuffix>
                                    <mat-icon matDatepickerToggleIcon>clear</mat-icon>
                                </mat-datepicker-toggle>
                            }
                            <mat-datepicker-toggle [for]="revealPicker" matIconSuffix></mat-datepicker-toggle>
                            <mat-datepicker #revealPicker></mat-datepicker>
                            <mat-hint>Select a date and time for the post to be revealed</mat-hint>
                        </mat-form-field>
                        <app-time-picker [appearance]="'outline'" [formControl]="revealTimeControl"
                                         label="Select reveal time"/>
                    </div>
                    <div [class.d-none]="!lockPostControl.value" class="d-flex flex-row gap-10 mt-20">
                        <mat-form-field appearance="outline" color="accent">
                            <mat-label>Select lock date</mat-label>
                            <input [formControl]="dateControl" [matDatepicker]="picker" matInput>
                            @if (dateControl.value) {
                                <mat-datepicker-toggle (click)="clearDate()" matIconSuffix>
                                    <mat-icon matDatepickerToggleIcon>clear</mat-icon>
                                </mat-datepicker-toggle>
                            }
                            <mat-datepicker-toggle [for]="picker" matIconSuffix></mat-datepicker-toggle>
                            <mat-datepicker #picker></mat-datepicker>
                        </mat-form-field>
                        <app-time-picker [appearance]="'outline'" [formControl]="timeControl" label="Select lock time"/>
                    </div>
                    <div [class.d-none]="!lockPostControl.value" class="d-flex flex-row gap-10 mt-10">
                        <mat-checkbox formControlName="off_comments_at_time">Lock comments</mat-checkbox>
                        <mat-checkbox formControlName="off_likes_at_time">Lock likes</mat-checkbox>
                        <mat-checkbox formControlName="off_dislikes_at_time">Lock dislikes</mat-checkbox>
                    </div>
                </form>
            }
            <div class="mt-10 mb-10 d-flex flex-col gap-10">
                <h3>Attachments:</h3>
                <div class="flex-row gap-10 flex-wrap">
                    <app-attachments (inUse)="setUse($event, 'docInUse')"
                                     [attachmentButtonText]="postAttachmentButtonText"
                                     (isValid)="docValidEmitter($event); this.authService.refreshUser();"
                                     (submitField)="setDocument($event)"
                                     [attachmentOptions]="['text','doc']"
                                     [class.attachmentInUse]="docInUse"
                                     [initialAttachment]="initialDocument">
                    </app-attachments>
                    <app-attachments (inUse)="setUse($event, 'urlInUse')"
                                     (isValid)="urlValidEmitter($event); this.authService.refreshUser();"
                                     (submitField)="setUrl($event)"
                                     [attachmentOptions]="['text','url']"
                                     [class.attachmentInUse]="urlInUse"
                                     [initialAttachment]="initialUrl">
                    </app-attachments>
                    <app-attachments (inUse)="setUse($event, 'audioInUse')"
                                     (isValid)="audioValidEmitter($event); this.authService.refreshUser();"
                                     (submitField)="setAudio($event)"
                                     [attachmentOptions]="['text','audio']"
                                     [class.attachmentInUse]="audioInUse"
                                     [initialAttachment]="initialAudio">
                    </app-attachments>
                    <app-attachments
                            (imagePositionChange)="this.createPostForm.patchValue({show_attachment_above_text: $event})"
                            (inUse)="setUse($event, 'imageInUse')"
                            (isValid)="imageValidEmitter($event); this.authService.refreshUser();"
                            (submitField)="setImages($event)"
                            [attachmentOptions]="['text','image']"
                            [class.attachmentInUse]="imageInUse"
                            [displayOverText]="!!this.editPost?.show_attachment_above_text"
                            [initialAttachment]="initialImages">
                    </app-attachments>
                    <app-attachments (inUse)="setUse($event, 'multiAttachmentInUse')"
                                     [voteButtonText]="postVoteButtonText"
                                     (isValid)="attachmentsValidEmitter($event); this.authService.refreshUser();"
                                     (removedAttachment)="removedPollOrHeatmap.emit($event)"
                                     (submitField)="getAttachmentField($event)"
                                     [attachmentOptions]="defaultAttachments"
                                     [class.attachmentInUse]="multiAttachmentInUse"
                                     [initialAttachment]="initialAttachment">
                    </app-attachments>
                    @if ((isSuperuser || hasCommunityRole(['full_admin', 'client']))) {
                        <app-attachments #attachmentComponentRef (inUse)="setUse($event, 'surveyPollInUse')"
                                         [voteButtonText]="postVoteButtonText"
                                         (isValid)="pollSurveyValidEmitter($event); this.authService.refreshUser();;"
                                         (removedAttachment)="removedPollOrHeatmap.emit($event)"
                                         (submitField)="setSurveyPoll($event)"
                                         (updatedPoll)="updatedPoll.emit()"
                                         [attachmentOptions]="pollAttachments"
                                         [class.attachmentInUse]="surveyPollInUse"
                                         [initialAttachment]="initialSurveyPoll">
                            @if (this.survey) {
                                <form [formGroup]="createPostForm">
                                    <mat-checkbox formControlName="iframe_survey">Display Survey Inline</mat-checkbox>
                                </form>
                            }
                        </app-attachments>
                    }
                </div>
            </div>
        </div>
    </mat-dialog-content>
    <mat-dialog-actions>
        <div class="w-100 d-flex flex-space-around pb-15">
            <button mat-raised-button color="warn" (click)="exitCheck()">
                Cancel
            </button>
            <div [matTooltip]="this.validateForm(this.createPostForm)">
                <button (click)="!editPost ? createPost() : update()"
                        [disabled]="!attachmentsValid || !docValid || !audioValid || !imageValid || !urlValid || !pollSurveyValid || this.createPostForm.invalid || sending"
                        color="accent" mat-raised-button>
                    {{ !editPost ? 'Start discussion' : 'Edit discussion' }}
                    @if (sending) {
                        <mat-icon>
                            <mat-spinner color="accent"></mat-spinner>
                        </mat-icon>
                    }
                </button>
            </div>
        </div>
        @if (sending) {
            <div class="w-100 d-flex pb-15 flex-col align-items-center gap-10">
                <div role="status" aria-live="polite">{{ uploadStatus }}</div>
                <mat-progress-bar
                        [mode]="uploadProgress !== null && uploadProgress! < 100 ? 'determinate' : 'indeterminate'"
                        [value]="uploadProgress ?? 0"
                        [attr.aria-label]="uploadStatus"></mat-progress-bar>
            </div>
        }
    </mat-dialog-actions>
} @else {
    <div class="d-flex flex-center align-items-center loader-container">
        <mat-spinner></mat-spinner>
    </div>
}
